import { Link } from "react-router-dom";
import { Video, Rotate3d, Headset, ArrowRight, Youtube, ArrowUpRight } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { useLang } from "@/i18n/LanguageContext";
import PageHero from "@/components/site/PageHero";
import { Reveal, SectionHead } from "@/components/site/Reveal";

const CHAPTER_ICONS = [Video, Rotate3d, Headset];
const CHAPTER_IMAGES = ["/assets/editorial-sunset.webp", "/assets/editorial-village.webp", "/assets/editorial-tower.webp"];
const CHAPTER_LINKS = ["/borghi/moncalvo", "/borghi/vignale-monferrato", "/eventi"];

const EsperienzePage = () => {
  const { t } = useLang();
  useSeo("esperienze");
  const chapters = t("esperienze.chapters");

  return (
    <div data-testid="esperienze-page">
      <PageHero
        testId="esperienze-hero"
        image="/assets/home-hero.webp"
        eyebrow={t("esperienze.eyebrow")}
        lines={[t("esperienze.heroA"), t("esperienze.heroB")]}
        subtitle={t("esperienze.heroSub")}
        height="h-[80vh]"
      />

      <section className="py-24 lg:py-32" data-testid="esperienze-chapters">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 space-y-24 lg:space-y-36">
          {chapters.map((c, i) => {
            const Icon = CHAPTER_ICONS[i];
            const reverse = i % 2 === 1;
            return (
              <div key={c.num} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-testid={`esperienze-chapter-${c.num}`}>
                <div className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}>
                  <Reveal>
                    <div className="flex items-center gap-5">
                      <span className="font-serif-display italic text-6xl text-[#C5A059]/40">{c.num}</span>
                      <Icon size={26} strokeWidth={1.2} className="text-[#9E7B36]" />
                    </div>
                    <h2 className="font-serif-display text-3xl sm:text-4xl mt-6">{c.title}</h2>
                    <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#666157] max-w-xl">{c.body}</p>
                    <Link
                      to={CHAPTER_LINKS[i]}
                      data-testid={`esperienze-chapter-cta-${c.num}`}
                      className="mt-8 inline-flex items-center gap-3 border border-[#1C1B18]/25 px-7 py-3.5 text-[11px] tracking-[0.25em] uppercase hover:border-[#C5A059] hover:text-[#9E7B36] transition-colors duration-500"
                    >
                      {c.cta} <ArrowRight size={13} />
                    </Link>
                  </Reveal>
                </div>
                <div className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}>
                  <Reveal delay={0.15}>
                    <div className="relative">
                      <div className={`absolute ${reverse ? "-bottom-4 -right-4" : "-top-4 -left-4"} w-full h-full border border-[#C5A059]/40 pointer-events-none hidden lg:block`} />
                      <img
                        src={CHAPTER_IMAGES[i]}
                        alt={c.title}
                        loading="lazy"
                        className="w-full object-cover photo-frame"
                        style={{ aspectRatio: "16/11" }}
                      />
                    </div>
                  </Reveal>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#121212] text-[#F7F5F0]" data-testid="esperienze-closing">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 text-center">
          <SectionHead dark center eyebrow="YouTube" title={<span className="italic">{t("esperienze.closingTitle")}</span>} />
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-2xl mx-auto text-[#F7F5F0]/65 text-base sm:text-lg leading-relaxed">{t("esperienze.closingBody")}</p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a
                href="https://www.youtube.com/@MonferratoImmersive"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="esperienze-youtube-cta"
                className="inline-flex items-center gap-3 bg-[#C5A059] text-[#121212] px-8 py-4 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-[#D8B46C] transition-colors duration-500"
              >
                <Youtube size={15} /> {t("esperienze.ctaChannel")} <ArrowUpRight size={13} />
              </a>
              <Link
                to="/#esplora"
                data-testid="esperienze-borghi-cta"
                className="inline-flex items-center gap-3 border border-[#F7F5F0]/30 px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:border-[#C5A059] hover:text-[#D8B46C] transition-colors duration-500"
              >
                {t("esperienze.ctaBorghi")}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default EsperienzePage;
